/**
 * Client render gate for card approvals (founder 2026-07-12).
 *
 * Fetches ONCE which cards this account must NOT show (the operator hid them, or
 * they're new and not yet approved) and exposes them to the card render path. The
 * server already applies the grandfather rule AND the demo/test/resettable
 * "see everything" exemption, so:
 *   - demo / test / resettable accounts → the list is always empty (render all).
 *   - real clients at ship → also empty (grandfather), so NOTHING changes.
 *   - a rejected card → appears here → hidden hard on real client accounts.
 *
 * Enforcement is applied at ONE choke point — the HideProvider/Hideable +
 * MetricCard render path (lib/hide.tsx) — which already gates every card. This
 * provider just supplies the per-page set of render-ids to hide.
 *
 * Fail-safe: any error / not-yet-loaded state = empty set = render everything.
 * Hiding a card the operator paid to review is a display nicety; accidentally
 * hiding a card a client should see is worse, so the default is always "show".
 */

import { createContext, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { CARD_CATALOG } from "@data-dashboard/shared";
import { useApiClient } from "./api-context.js";

/** Client-visible ⓘ override for one card: the "What it is" and/or "Note" text. */
export interface ExplainOverride {
  what?: string;
  note?: string;
}

interface CardApprovalState {
  /** Full catalog ids (e.g. "operations.refund-rate") hidden for this account. */
  hiddenIds: Set<string>;
  /** Operator rename overrides, keyed by the card's RENDER id (so a component
   *  that only knows its own renderId can look up its new title). Global —
   *  applies to every account incl. the demo. Empty when nothing is renamed. */
  nameOverrides: Map<string, string>;
  /** Operator ⓘ overrides, keyed by render id — the client-visible "What it is" /
   *  "Note" text. Global. Empty when nothing is overridden. */
  explainOverrides: Map<string, ExplainOverride>;
  /** Operator company-wide badge overrides, keyed by render id (true/false).
   *  Global. Empty when nothing is overridden. */
  companyWideOverrides: Map<string, boolean>;
}

const EMPTY: Set<string> = new Set();
const EMPTY_NAMES: Map<string, string> = new Map();
const EMPTY_EXPLAIN: Map<string, ExplainOverride> = new Map();
const EMPTY_CW: Map<string, boolean> = new Map();
const Ctx = createContext<CardApprovalState>({ hiddenIds: EMPTY, nameOverrides: EMPTY_NAMES, explainOverrides: EMPTY_EXPLAIN, companyWideOverrides: EMPTY_CW });

/**
 * Wrap the authenticated client shell once (above the per-page HideProvider) so
 * the hidden set is fetched a single time and shared by every page. Absent (the
 * public /demo showcase, card-library previews) → the default empty set, i.e.
 * render everything.
 */
export function CardApprovalProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const api = useApiClient();
  const q = useQuery({
    queryKey: ["cards-approval"],
    queryFn: () =>
      api.get<{
        hiddenCardIds: string[];
        nameOverrides?: Record<string, string>;
        explainOverrides?: Record<string, ExplainOverride>;
        companyWideOverrides?: Record<string, boolean>;
      }>("/dashboard/cards-approval"),
    staleTime: 5 * 60_000,
  });
  const value = useMemo<CardApprovalState>(
    () => ({
      hiddenIds: q.data?.hiddenCardIds?.length ? new Set(q.data.hiddenCardIds) : EMPTY,
      nameOverrides:
        q.data?.nameOverrides && Object.keys(q.data.nameOverrides).length
          ? new Map(Object.entries(q.data.nameOverrides))
          : EMPTY_NAMES,
      explainOverrides:
        q.data?.explainOverrides && Object.keys(q.data.explainOverrides).length
          ? new Map(Object.entries(q.data.explainOverrides))
          : EMPTY_EXPLAIN,
      companyWideOverrides:
        q.data?.companyWideOverrides && Object.keys(q.data.companyWideOverrides).length
          ? new Map(Object.entries(q.data.companyWideOverrides))
          : EMPTY_CW,
    }),
    [q.data],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The operator's rename for the card with this RENDER id, or undefined. */
export function useCardNameOverride(renderId: string | null | undefined): string | undefined {
  const { nameOverrides } = useContext(Ctx);
  return renderId ? nameOverrides.get(renderId) : undefined;
}

/** The operator's ⓘ override (what / note) for this RENDER id, or undefined. */
export function useExplainOverride(renderId: string | null | undefined): ExplainOverride | undefined {
  const { explainOverrides } = useContext(Ctx);
  return renderId ? explainOverrides.get(renderId) : undefined;
}

/** The operator's company-wide badge override for this RENDER id, or undefined
 *  (= use the card's coded default). */
export function useCompanyWideOverride(renderId: string | null | undefined): boolean | undefined {
  const { companyWideOverrides } = useContext(Ctx);
  return renderId ? companyWideOverrides.get(renderId) : undefined;
}

/**
 * The set of RENDER ids to hide on a given page (the ids Hideable/MetricCard key
 * on). Translates the account's hidden catalog-ids into this page's render-ids
 * via the shared catalog. Empty unless the account is a real client with hidden
 * cards on this exact page.
 */
export function useForcedHiddenRenderIds(pageKey: string): Set<string> {
  const { hiddenIds } = useContext(Ctx);
  return useMemo(() => {
    if (hiddenIds.size === 0) return EMPTY;
    const out = new Set<string>();
    for (const c of CARD_CATALOG) {
      if (!hiddenIds.has(c.id)) continue;
      // A merged card can render on several pages under different renderIds —
      // hide the placement(s) that belong to THIS page.
      for (const p of c.placements) {
        if (p.pageKey === pageKey) out.add(p.renderId);
      }
    }
    return out.size ? out : EMPTY;
  }, [hiddenIds, pageKey]);
}
